import { useCallback, useState } from 'react';
import { useUpdateFeature } from '@/features/features/hooks/features';
import { useReleasesQuery } from '@/features/releases/hooks/releases';
import { useApiErrorMessage } from '@/shared/data/errors';
import { useDialogFormState } from '@/ui/hooks/useDialogFormState';
import { useFormErrors } from '@/ui/hooks/useFormErrors';
import type { FeatureDto, ReleaseListItemDto } from '@flowqa/shared';

type FeatureEditValues = {
  name: string;
  status: FeatureDto['status'];
  projectId: string;
  releaseId: string;
  plannedFor: string;
  releasedAt: string;
  tags: string[];
};

type FeatureEditDialogState = {
  editErrors: ReturnType<typeof useFormErrors<FeatureEditValues>>;
  editForm: ReturnType<typeof useDialogFormState<FeatureEditValues>>;
  editReleaseOptions: ReleaseListItemDto[];
  editSaving: boolean;
  editTarget: FeatureDto | null;
  onEditClose: () => void;
  onEditRow: (row: FeatureDto) => void;
  onEditSave: () => Promise<void>;
};

const toDateInputValue = (value: Date | string | null | undefined): string => {
  if (value === null || value === undefined) return '';
  const date = value instanceof Date ? value : new Date(value);
  return Number.isNaN(date.valueOf()) ? '' : date.toISOString().slice(0, 10);
};

export const useFeatureEditDialog = (): FeatureEditDialogState => {
  const toErrorMessage = useApiErrorMessage();
  const [editTarget, setEditTarget] = useState<FeatureDto | null>(null);
  const updateFeature = useUpdateFeature();
  const editForm = useDialogFormState<FeatureEditValues>({
    name: '',
    status: 'draft',
    projectId: '',
    releaseId: '',
    plannedFor: '',
    releasedAt: '',
    tags: [],
  });
  const editErrors = useFormErrors(editForm);
  const releasesQuery = useReleasesQuery(editTarget?.projectId ?? null, {
    enabled: editTarget !== null,
  });

  const onEditRow = useCallback(
    (row: FeatureDto): void => {
      setEditTarget(row);
      editForm.setField('name', row.name);
      editForm.setField('status', row.status);
      editForm.setField('projectId', row.projectId);
      editForm.setField('releaseId', row.releaseId ?? '');
      editForm.setField('plannedFor', toDateInputValue(row.plannedFor));
      editForm.setField('releasedAt', toDateInputValue(row.releasedAt));
      editForm.setField('tags', row.tags ?? []);
    },
    [editForm],
  );

  const onEditClose = useCallback((): void => {
    if (updateFeature.isPending) return;
    editForm.handleClose(() => {
      setEditTarget(null);
    });
  }, [editForm, updateFeature.isPending]);

  const onEditSave = useCallback(async (): Promise<void> => {
    if (!editTarget) return;
    const valid = editForm.validateRequired('name');
    if (!valid) return;
    try {
      await updateFeature.mutateAsync({
        id: editTarget.id,
        name: editForm.values.name,
        status: editForm.values.status,
        projectId: editForm.values.projectId,
        releaseId: editForm.values.releaseId.trim() || null,
        plannedFor: editForm.values.plannedFor || null,
        releasedAt: editForm.values.releasedAt || null,
        tags: editForm.values.tags,
      });
      editForm.handleClose(() => {
        setEditTarget(null);
      });
    } catch (error) {
      if (!editForm.applyApiErrors(error)) {
        editForm.setSubmitError(toErrorMessage(error));
      }
    }
  }, [editForm, editTarget, toErrorMessage, updateFeature]);

  return {
    editErrors,
    editForm,
    editReleaseOptions: releasesQuery.data?.items ?? [],
    editSaving: updateFeature.isPending,
    editTarget,
    onEditClose,
    onEditRow,
    onEditSave,
  };
};
